import { PaymentType } from "../../../../presentation/models/enum/PaymentType"
import { Duration } from "../../../models/common/Duration"

/**
 * Represents the request body for creating a terminal.
 */
export interface CreateTerminal {
    /**
     * The franchise where the terminal is being created
     */
    franchiseId: string

    /**
     * The name of the terminal.
     */
    name: string

    /**
     * The current operational status of the terminal.
     */
    status: TerminalStatus

    /**
     * The latitude coordinate of the terminal's location.
     */
    latitude: number

    /**
     * The longitude coordinate of the terminal's location.
     */
    longitude: number

    /**
     * The physical address of the terminal.
     */
    address: {
        /**
         * The street address of the terminal.
         */
        street: string
        /**
         * The city where the terminal is located.
         */
        city: string
        /**
         * The state where the terminal is located.
         */
        state: string
        /**
         * The postal code of the terminal location.
         */
        postalCode: string
    }

    /**
     *  Base pricing for using the terminal services.
     */
    basePrice?: Pricing

    /**
     * Additional pricing options applicable to the terminal.
     */
    additionalPrices?: Pricing

    /**
     * The list of lockers available in the terminal.
     */
    lockers: Locker[]

    /**
     * The Bluetooth MAC address associated with the terminal.
     */
    bluetoothMacId: string

    /**
     * Indicates whether a payment is required before dropping items.
     */
    dropPaymentRequired: boolean

    /**
     * The terminal type which specifies which type of terminal it is.
     * @example - Face-Id or Non-FaceId or Freshkart or Subscription.
     */
    terminalType: TerminalType

    /**
       * The payment type which specifies which type of payment it is.
       * @example - During Drop Off or During Pick Up.
       */
    paymentType: PaymentType

    /**
     * The number of manual sessions allowed per month for this terminal.
     */
    allowedManualSessionsPerMonth: number

    /**
     * Specifies if the terminal requires login while pickUp or not.
     */
    pickupLoginRequired: boolean // TODO: Is this still needed?

    /**
     * The list of countries where the terminal is supported.
     */
    supportedCountries: {
        /**
         * The name of the country.
         */
        name: string
        /**
         * The country code (e.g., "US", "IN").
         */
        /**
         * A list of assigned runners responsible for the terminal.
         */
        code: string
    }[]

    /**
     * The contact support number for the terminal.
     */
    supportNumber: string

    /**
     * The type of hardware communication used by the terminal.
     */
    hardwareCommunication: CommunicationType

    /**
     * Holds the video url's for terminal to play video on app side
     */
    videos?: {
        source: string
        description: string
        isActive: boolean
    }[]
}

/**
 * Locker interface to represent the document structure for a locker.
 * 
 * This interface defines the structure of a locker document in the database, 
 * including details such as its size, status, locker number, dimensions, maintenance logs,
 * and terminal association.
 * 
 * @interface Locker
 * @extends Document
 */
export interface Locker {
    /**
     * The size of the locker, defined by the LockerSize enum.
     * @type {LockerSize}
     */
    size: LockerSize

    /**
     * The number of lockers to create.
     * @type {number}
     */
    count: number

    /**
     * The dimensions of the locker, including width, height, and depth.
     * @type {{ width: number, height: number, depth: number }}
     */
    dimensions: {
        width: number
        height: number
        depth: number
    }
}

/**
* Enum representing the different statuses of a terminal.
* 
* This enum is used to define the various states that a terminal can be in. It helps in managing the terminal's 
* operation status, whether it is actively functioning, inactive, or under maintenance.
* 
* Enum Values:
* - `ACTIVE`: The terminal is currently active and operational.
* - `INACTIVE`: The terminal is not currently active.
* - `MAINTENANCE`: The terminal is under maintenance and not available for use.
* 
* @enum {string}
*/
export enum TerminalStatus {
    /**
     * Represents a terminal that is active and operational.
     */
    ACTIVE = "Active",

    /**
     * Represents a terminal that is inactive and not available for use.
     */
    INACTIVE = "Inactive",

    /**
     * Represents a terminal that is under maintenance and cannot be used.
     */
    MAINTENANCE = "Maintenance"
}

/**
* Enum representing different types of terminals.
*/
export enum TerminalType {
    /**
     * Terminal requires Face ID authentication.
     */
    FACE_ID = "Face-Id",

    /**
     * Terminal does not require Face ID authentication.
     */
    NON_FACE_ID = "Non-FaceId",

    /**
     * Terminal type for Freshkart operations.
     */
    FRESHKART = "FreshKart",

    /**
     * Terminal type used for subscription-based operations.
     */
    SUBSCRIPTION = "Subscription",

    /**
     * Terminal type used for quick-drop-based operations.
     */
    QUICKDROP = "Quick-Drop"
}

/**
* Enum representing the types of communication used by a terminal.
* 
* This enum is used to define the various communication types supported by a terminal.
* It helps categorize the method of communication a terminal will use for connectivity.
* 
* Enum Values:
* - `BLUETOOTH`: Represents Bluetooth communication type.
* - `WIFI`: Represents WiFi communication type.
* - `CELLULAR`: Represents Cellular communication type.
* 
* @enum {string}
*/
export enum CommunicationType {
    /**
     * Represents the Bluetooth communication type.
     */
    BLUETOOTH = "Bluetooth",

    /**
     * Represents the WiFi communication type.
     */
    WIFI = "WiFi",

    /**
     * Represents the Cellular communication type.
     */
    CELLULAR = "Cellular",

    /**
     * Represents the MOCKED DEVICE communication type.
     */
    MOCKEDDEVICE = "Mocked_Device"
}

/**
* Interface representing the price details for a particular locker size.
* 
* The `Price` interface is used to define the price of a locker based on its size. 
* The size is defined by the LockerSize enum, and the price is represented as a number.
* 
* @interface LockerPrice
*/
export interface LockerPrice {
    /**
     * The size of the locker, defined by the LockerSize enum.
     * @type {LockerSize}
     */
    size: LockerSize

    /**
     * The price for the locker size.
     * @type {number}
     */
    price: number
}

/**
 * Enum representing the different sizes of lockers.
 * 
 * This enum is used to define the different sizes of lockers available for use in a system.
 * It helps categorize the size of lockers based on the available space and usage requirements.
 * 
 * Enum Values:
 * - `SMALL`: Represents a small-sized locker.
 * - `MEDIUM`: Represents a medium-sized locker.
 * - `LARGE`: Represents a large-sized locker.
 * - `X_L`: Represents an extra-large locker.
 * - `CUSTOM`: Represents a custom-sized locker, typically with user-defined dimensions.
 * 
 * @enum {string}
 */
export enum LockerSize {
    /**
     * Represents a small locker size.
     */
    SMALL = "SMALL",

    /**
     * Represents a medium locker size.
     */
    MEDIUM = "MEDIUM",

    /**
     * Represents a large locker size.
     */
    LARGE = "LARGE",

    /**
     * Represents an extra-large locker size.
     */
    X_L = "X_L",

    /**
     * Represents a custom locker size, often user-defined.
     */
    CUSTOM = "CUSTOM"
}

/**
 * Interface representing the pricing details for lockers.
 * 
 * The `Pricing` interface defines a pricing plan based on the duration (e.g., hour, day, week, month).
 * It includes an operator (optional) for price calculation and an array of prices corresponding 
 * to different locker sizes.
 * 
 * @interface Pricing
 */
export interface Pricing {
    /**
     * The unit of duration (e.g., 'minute', 'hour', 'day', 'week', or 'month').
     * @type {'minute' | 'hour' | 'day' | 'week' | 'month'}
     */
    duration: Duration

    /**
     * The time associated with the pricing plan.
     * @type {number}
     */
    time: number

    /**
     * The operator for price calculation (optional). Can be addition, subtraction, multiplication, or division.
     * @type {'+' | '-' | '*' | '/'}
     */
    operator?: '+' | '-' | '*' | '/'

    /**
     * Array of prices for different locker sizes.
     * @type {Price[]}
     */
    prices: LockerPrice[]
}

/**
 * Enum representing the different statuses of a locker.
 * 
 * This enum is used to define the various states that a locker can be in. It is essential for tracking
 * and managing locker availability and maintenance status within a system.
 * 
 * Enum Values:
 * - `AVAILABLE`: The locker is available for use.
 * - `UNAVAILABLE`: The locker is not available for use.
 * - `IN_USE`: The locker is currently in use.
 * - `MAINTENANCE`: The locker is under maintenance and cannot be used.
 * 
 * @enum {string}
 */
export enum LockerStatus {
    /**
     * Represents a locker that is available for use.
     */
    AVAILABLE = "Available",

    /**
     * Represents a locker that is unavailable for use.
     */
    UNAVAILABLE = "Unavailable",

    /**
     * Represents a locker that is currently in use.
     */
    IN_USE = "In Use",

    /**
     * Represents a locker that is under maintenance.
     */
    MAINTENANCE = "Maintenance"
}